"use client"

import { Head, router, useForm } from "@inertiajs/react"

import { Breadcrumbs } from "@/components/ui/breadcrumbs"
import PublicLayout from "@/layouts/public-layout"
import type { Playlist, Tag, Track } from "@/types"
import type { ContentBodyTypeEnum } from "@/types"

import TrackForm from "./_components/track-form"
import { mergeContentBodyAttributes } from "./_helpers/merge-content-body-attributes"

interface EditTrackProps {
  playlist: Playlist
  track: Track
  availableTags: Tag[]
  content_type: ContentBodyTypeEnum
}

/**
 * Edit Track component.
 * @param playlist - The playlist.
 * @param track - The track.
 * @param content_type - The content type.
 * @returns The Edit Track component.
 */
export default function EditTrack({
  playlist,
  track,
  availableTags,
  content_type,
}: EditTrackProps) {
  const post = track.post
  const initialTags = track.tags ?? []
  const mergedContentBodyAttributes = mergeContentBodyAttributes(
    post?.content_body,
    content_type,
  )

  const { data, setData, patch, processing, errors } = useForm({
    track: {
      notes: track.notes ?? "",
      tag_ids: initialTags.map((tag) => tag.id),
      post_attributes: {
        id: post?.id,
        title: post?.title ?? "",
        creator: post?.creator ?? "",
        canonical_url: post?.canonical_url ?? "",
        thumbnail_url: post?.thumbnail_url ?? "",
        published: post?.published ?? false,
        content_body_type: content_type,
        content_body_attributes: mergedContentBodyAttributes,
      },
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const hasImageFile =
      data.track.post_attributes.content_body_attributes?.image instanceof File
    patch(
      `/playlists/${playlist.slug ?? playlist.id}/tracks/${track.id}?content_type=${data.track.post_attributes.content_body_type}`,
      {
        forceFormData: hasImageFile,
      },
    )
  }

  return (
    <PublicLayout headerVariant="light">
      <Head title="Edit Track" />
      <section className="bg-secondary mb-3 pt-6 pb-8">
        <div className="max-w-container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <div className="mb-3">
            <Breadcrumbs maxVisibleItems={4} />
          </div>

          {/* Page header */}
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
              Edit Track
            </h1>
            <p className="text-md text-tertiary">
              Update content that helped you, along with your perspective on why
              and how it helped.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-container mx-auto mt-6 mb-8 flex min-h-[calc(80vh)] flex-1 flex-col gap-5 px-4 lg:px-8">
        <TrackForm
          data={data}
          setData={setData}
          errors={errors}
          availableTags={availableTags}
          initialTags={initialTags}
          processing={processing}
          submitLabel="Update Track"
          onSubmit={submit}
          onCancel={() =>
            router.visit(`/playlists/${playlist.slug ?? playlist.id}`)
          }
        />
      </main>
    </PublicLayout>
  )
}
